/** Passive, approximate microphone energy timing. Never controls provider VAD or retains audio. */
export type LocalSpeechTiming = {
  start: (itemId: unknown) => void;
  stop: (itemId: unknown) => number | undefined;
  reset: () => void;
  close: () => void;
};

const SAMPLE_MS = 20;
const MAX_SAMPLE_GAP_MS = 100;
// RMS 0.015 (~-36.5 dBFS) is a fixed energy threshold, not a speech classifier.
const VOICED_RMS = 0.015;
const MIN_QUIET_MS = 60;
const MAX_STOP_AGE_MS = 5000;
const noop = (): LocalSpeechTiming => ({ start: () => undefined, stop: () => undefined,
  reset: () => undefined, close: () => undefined });

export const createLocalSpeechTiming = (stream: MediaStream): LocalSpeechTiming => {
  let context: AudioContext | undefined;
  let source: MediaStreamAudioSourceNode | undefined;
  let analyser: AnalyserNode | undefined;
  try {
    if (typeof AudioContext === 'undefined') return noop();
    context = new AudioContext();
    analyser = context.createAnalyser();
    analyser.fftSize = 512;
    source = context.createMediaStreamSource(stream);
    source.connect(analyser); // No output connection; no microphone monitoring/playback.
    // Autoplay policy may refuse this; telemetry stays unavailable without delaying Voice.
    try { void context.resume().catch(() => undefined); } catch { /* Optional diagnostics. */ }
    const samples = new Float32Array(analyser.fftSize);
    let history: Array<{ at: number; voiced: boolean }> = [];
    let active: { id: string; since: number; invalid: boolean } | undefined;
    let lastSample: number | undefined;
    let closed = false;
    const available = () => !closed && document.visibilityState === 'visible'
      && context?.state === 'running'
      && stream.getAudioTracks().some((track) => track.enabled && !track.muted && track.readyState === 'live');
    const reset = () => { history = []; lastSample = undefined; active = undefined; };
    const sample = () => {
      const now = performance.now();
      if (!available() || (lastSample !== undefined && now - lastSample > MAX_SAMPLE_GAP_MS)) {
        if (active) active.invalid = true;
        history = [];
        lastSample = undefined;
        return;
      }
      try {
        analyser!.getFloatTimeDomainData(samples);
        let sum = 0;
        for (let index = 0; index < samples.length; index += 1) sum += samples[index] ** 2;
        history.push({ at: now, voiced: Math.sqrt(sum / samples.length) >= VOICED_RMS });
        history = history.filter((entry) => now - entry.at <= 7000);
        lastSample = now;
      } catch { reset(); }
    };
    const timer = window.setInterval(sample, SAMPLE_MS);
    const onVisibility = () => { if (document.visibilityState !== 'visible') reset(); };
    document.addEventListener('visibilitychange', onVisibility);
    return {
      start: (itemId) => {
        // Provider item IDs pair the events; unmatched or missing events cannot identify an utterance.
        active = typeof itemId === 'string' && itemId && available()
          ? { id: itemId, since: performance.now() - 250, invalid: false } : undefined;
      },
      stop: (itemId) => {
        const utterance = active;
        active = undefined;
        const now = performance.now();
        if (!utterance || utterance.invalid || utterance.id !== itemId || !available()
          || lastSample === undefined || now - lastSample > MAX_SAMPLE_GAP_MS) return undefined;
        const relevant = history.filter((entry) => entry.at >= utterance.since);
        history = [];
        let consecutive = 0;
        let sustained = false;
        let lastVoice: number | undefined;
        relevant.forEach((entry) => {
          consecutive = entry.voiced ? consecutive + 1 : 0;
          if (consecutive >= 3) sustained = true;
          if (entry.voiced) lastVoice = entry.at;
        });
        if (!sustained || lastVoice === undefined || now - lastVoice > MAX_STOP_AGE_MS
          || lastSample - lastVoice < MIN_QUIET_MS) return undefined;
        return lastVoice;
      },
      reset,
      close: () => {
        if (closed) return;
        closed = true;
        window.clearInterval(timer);
        document.removeEventListener('visibilitychange', onVisibility);
        reset();
        try { source?.disconnect(); analyser?.disconnect(); } catch { /* Optional diagnostics. */ }
        try { void context?.close().catch(() => undefined); } catch { /* Optional diagnostics. */ }
      },
    };
  } catch {
    try { source?.disconnect(); analyser?.disconnect(); } catch { /* Optional diagnostics only. */ }
    try { void context?.close().catch(() => undefined); } catch { /* Partial AudioContext implementation. */ }
    return noop();
  }
};
